import { useAuth } from "@clerk/clerk-react";
import { ReactNode, useEffect, useState } from "react";
import { APICacheProviderContext } from "./useHooks";
import { v4 as uuidv4 } from "uuid";
import { SoapNoteData } from "@neum/frontend-shared/types/metagraphTypes";
import { TemplateObject } from "../utils/TemplateObject";
import { SectionType } from "@neum/frontend-shared/types/templateTypes";
import { downloadSoapNoteData, getNotesMetadata } from "../serverActions/sessionMetagraphApis";
import { getTemplateDefinition, getTemplateObject } from "../serverActions/templateApis";

export interface APICacheContextType {
    // SOAP Notes cache
    downloadSoapDataWithCache: (
        sessionId: string,
        noteId: string
    ) => Promise<SoapNoteData | undefined>;
    updateSoapCache: (
        sessionId: string,
        noteId: string,
        data: SoapNoteData
    ) => void;
    clearSoapCache: (sessionId?: string, noteId?: string) => void;

    // Template cache
    fetchTemplateWithCache: (
        templateId: string
    ) => Promise<TemplateObject | undefined>;
    fetchTemplateDefinitionWithCache: (
        templateId: string
    ) => Promise<SectionType[]>;

    // Active sessions note ID cache
    getNoteIdForActiveSession: (sessionId: string) => Promise<string>;
    clearActiveSessionNoteIdCache: (sessionId?: string) => void;
}

interface APICacheProviderProps {
    children: ReactNode;
}

export const APICacheProvider: React.FC<APICacheProviderProps> = ({
    children,
}) => {
    // Common
    const { getToken, orgId } = useAuth();
    const token = async () => await getToken({ template: "supabase" }) ?? ""

    useEffect(() => {
        // Clear all caches when the organization changes
        setSoapNotesCache({});
        setTemplateCache({});
        setTemplateDefinitionCache({});
    }, [orgId, getToken]);

    async function getCachedOrFetch<T>(
        cacheKey: string,
        cache: Record<string, T | undefined>,
        fetcher: () => Promise<T>,
        setCacheFn: (
            updater: (
                prev: Record<string, T | undefined>
            ) => Record<string, T | undefined>
        ) => void
    ): Promise<T> {
        const cachedData = cache[cacheKey];

        if (cachedData) {
            return cachedData; // Return cached data if available
        } else {
            const fetchedData = await fetcher();

            // Update cache
            setCacheFn((prev) => ({
                ...prev,
                [cacheKey]: fetchedData,
            }));

            return fetchedData;
        }
    }

    function updateCache<T>(
        cacheKey: string,
        data: T,
        setCacheFn: (
            updater: (
                prev: Record<string, T | undefined>
            ) => Record<string, T | undefined>
        ) => void
    ) {
        setCacheFn((prev) => ({
            ...prev,
            [cacheKey]: {
                ...(prev[cacheKey] || {}),
                ...data,
            },
        }));
    }

    function clearCache<T>(
        setCacheFn: (
            updater: (
                prev: Record<string, T | undefined>
            ) => Record<string, T | undefined>
        ) => void,
        cacheKey?: string
    ) {
        if (cacheKey) {
            setCacheFn((prev) => ({
                ...prev,
                [cacheKey]: undefined,
            }));
        } else {
            setCacheFn(() => ({}));
        }
    }

    // SOAP Notes cache
    const [soapNotesCache, setSoapNotesCache] = useState<
        Record<string, SoapNoteData | undefined>
    >({});

    function downloadSoapDataWithCache(
        sessionId: string,
        noteId: string
    ): Promise<SoapNoteData | undefined> {
        return getCachedOrFetch<SoapNoteData>(
            `${sessionId}_${noteId}`, // Cache key
            soapNotesCache,
            async () =>
                downloadSoapNoteData({
                    token: await token(),
                    path: { session_id: sessionId, node_id: noteId },
                }),
            setSoapNotesCache
        );
    }

    function updateSoapCache(
        sessionId: string,
        noteId: string,
        data: SoapNoteData
    ) {
        const cacheKey = `${sessionId}_${noteId}`;
        updateCache(cacheKey, data, setSoapNotesCache);
    }

    function clearSoapCache(sessionId?: string, noteId?: string) {
        const cacheKey =
            sessionId && noteId ? `${sessionId}_${noteId}` : undefined;
        clearCache(setSoapNotesCache, cacheKey);
    }

    // Template cache
    const [templateCache, setTemplateCache] = useState<
        Record<string, TemplateObject | undefined>
    >({});

    function fetchTemplateWithCache(
        templateId: string
    ): Promise<TemplateObject> {
        return getCachedOrFetch<TemplateObject>(
            templateId, // Cache key
            templateCache,
            async () => {
                const templateResponse = await getTemplateObject({
                    token: await token(),
                    path: { template_id: templateId.split("/")[1] },
                });
                return templateResponse.template as TemplateObject;
            },
            setTemplateCache
        );
    }

    // Template Definition cache
    const [templateDefinitionCache, setTemplateDefinitionCache] = useState<
        Record<string, SectionType[] | undefined>
    >({});

    function fetchTemplateDefinitionWithCache(
        templateId: string
    ): Promise<SectionType[]> {
        return getCachedOrFetch<SectionType[]>(
            templateId,
            templateDefinitionCache,
            async () => {
                const template = await getTemplateDefinition({
                    token: await token(),
                    path: { template_id: templateId },
                });
                return template as SectionType[];
            },
            setTemplateDefinitionCache
        );
    }

    // Active sessions note ID cache
    const [activeSessionNoteIdCache, setActiveSessionNoteIdCache] = useState<
        Record<string, string | undefined>
    >({});

    function getNoteIdForActiveSession(sessionId: string) {
        return getCachedOrFetch<string>(
            sessionId,
            activeSessionNoteIdCache,
            async () => {
                const notesMetadata = await getNotesMetadata({
                    token: await token(),
                    path: { session_id: sessionId },
                });
                if (notesMetadata.length > 0) {
                    return notesMetadata[0].id;
                }

                return uuidv4(); // Return a new UUID if no notes are found
            },
            setActiveSessionNoteIdCache
        );
    }

    function clearActiveSessionNoteIdCache(sessionId?: string) {
        clearCache(setActiveSessionNoteIdCache, sessionId);
    }

    return (
        <APICacheProviderContext.Provider
            value={{
                downloadSoapDataWithCache,
                updateSoapCache,
                clearSoapCache,
                fetchTemplateWithCache,
                fetchTemplateDefinitionWithCache,
                getNoteIdForActiveSession,
                clearActiveSessionNoteIdCache,
            }}
        >
            {children}
        </APICacheProviderContext.Provider>
    );
};
